import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { getKennelStatusLabel } from "@/lib/badge-styles";
import { getEvalShortLabel } from "@mspca-beti/shared";

export interface HistoryEntry {
  status: string | null;
  substatus?: string | null;
  startDate: string;
  endDate: string | null;
  durationDays: number;
  isApprox?: boolean;
}

export interface EvalHistoryEntry {
  status: string | null;
  startDate: string;
  endDate: string | null;
  durationDays: number;
}

export interface StatusHistory {
  kennelStatusHistory: HistoryEntry[];
  evalStatusHistory: EvalHistoryEntry[];
}

const KENNEL_STATUS_COLORS: Record<string, string> = {
  AVAILABLE: "bg-[hsl(var(--status-available)/0.15)] text-[hsl(var(--status-available))]",
  UNAVAIL: "bg-[hsl(var(--status-unavailable)/0.15)] text-[hsl(var(--status-unavailable))]",
  STRAY_WAIT: "bg-[hsl(var(--status-stray-wait)/0.15)] text-[hsl(var(--status-stray-wait))]",
  EVALUATION: "bg-[hsl(var(--status-evaluation)/0.15)] text-[hsl(var(--status-evaluation))]",
};


const EVAL_STATUS_COLORS: Record<string, string> = {
  complete: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  needs_review: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  needs_recs: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  incomplete: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  unknown: "bg-muted text-muted-foreground",
  error: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

function formatDuration(days: number, isApprox?: boolean): string {
  const prefix = isApprox ? "~" : "";
  if (days === 0) return `${prefix}<1d`;
  if (days === 1) return `${prefix}1d`;
  if (days < 30) return `${prefix}${days}d`;
  if (days < 60) return `${prefix}${Math.round(days / 7)}wk`;
  return `${prefix}${Math.round(days / 30)}mo`;
}

function formatDateShort(dateStr: string): string {
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const d = new Date(isDateOnly ? dateStr + "T00:00:00" : dateStr);
  return isNaN(d.getTime()) ? dateStr : format(d, "MMM d");
}

const ALL_KENNEL_STATUSES = ["EVALUATION", "STRAY_WAIT", "UNAVAIL", "AVAILABLE"] as const;

export function KennelStatusStrip({ segments }: { segments: HistoryEntry[] }) {
  const statusMap = new Map<string, { totalDays: number; isCurrent: boolean; isApprox: boolean; substatus?: string | null }>();
  for (const seg of segments) {
    const key = (seg.status ?? "").toUpperCase();
    const existing = statusMap.get(key);
    statusMap.set(key, {
      totalDays: (existing?.totalDays ?? 0) + seg.durationDays,
      isCurrent: (existing?.isCurrent ?? false) || seg.endDate === null,
      isApprox: (existing?.isApprox ?? false) || (seg.isApprox ?? false),
      substatus: seg.endDate === null ? seg.substatus : (existing?.substatus ?? seg.substatus),
    });
  }

  return (
    <div
      className="flex gap-2 overflow-x-auto pb-1"
      data-testid="kennel-status-strip"
    >
      {ALL_KENNEL_STATUSES.map((statusKey, idx) => {
        const data = statusMap.get(statusKey);
        const isActive = !!data && data.totalDays > 0;
        const isCurrent = data?.isCurrent ?? false;
        const colorClass = isActive
          ? KENNEL_STATUS_COLORS[statusKey] || "bg-muted text-muted-foreground"
          : "bg-muted/40 text-muted-foreground/50";
        return (
          <div
            key={statusKey}
            className={`shrink-0 flex flex-col items-center gap-1 rounded-md border px-3 py-2 min-w-[80px] text-center ${
              isCurrent
                ? "border-primary/50 bg-primary/5"
                : isActive
                ? "opacity-70"
                : "opacity-50"
            }`}
            data-testid={`kennel-status-chip-${idx}`}
          >
            <span className="text-sm font-semibold tabular-nums">
              {isActive ? formatDuration(data!.totalDays, data!.isApprox) : "0d"}
            </span>
            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-sm ${colorClass}`}>
              {getKennelStatusLabel(statusKey)}
            </span>
            {isActive && data?.substatus && (
              <span className="text-[10px] text-muted-foreground leading-none">
                {data.substatus}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function EvalStatusStrip({ animalId }: { animalId: string }) {
  const { data, isLoading } = useQuery<StatusHistory>({
    queryKey: ["/api/animals", animalId, "status-history"],
    queryFn: async () => {
      const res = await fetch(`/api/animals/${animalId}/status-history`);
      if (!res.ok) throw new Error("Failed to fetch status history");
      return res.json();
    },
    enabled: !!animalId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span className="text-xs">Loading...</span>
      </div>
    );
  }

  const history = (data?.evalStatusHistory ?? []).filter(
    (e) => e.status !== null
  );

  const statusMap = new Map<string, { totalDays: number; isCurrent: boolean }>();
  for (const entry of history) {
    const key = entry.status ?? "unknown";
    const existing = statusMap.get(key);
    statusMap.set(key, {
      totalDays: (existing?.totalDays ?? 0) + entry.durationDays,
      isCurrent: (existing?.isCurrent ?? false) || entry.endDate === null,
    });
  }

  const ALL_EVAL_STATUSES = ["incomplete", "needs_review", "needs_recs", "complete"] as const;

  return (
    <div
      className="flex gap-2 overflow-x-auto pb-1"
      data-testid="eval-status-strip"
    >
      {ALL_EVAL_STATUSES.map((statusKey, idx) => {
        const entry = statusMap.get(statusKey);
        const isActive = !!entry && entry.totalDays > 0;
        const isCurrent = entry?.isCurrent ?? false;
        const colorClass = isActive
          ? EVAL_STATUS_COLORS[statusKey] || "bg-muted text-muted-foreground"
          : "bg-muted/40 text-muted-foreground/50";
        const label = getEvalShortLabel(statusKey);
        return (
          <div
            key={statusKey}
            className={`shrink-0 flex flex-col items-center gap-1 rounded-md border px-3 py-2 min-w-[80px] text-center ${
              isCurrent
                ? "border-primary/50 bg-primary/5"
                : isActive
                ? "opacity-70"
                : "opacity-50"
            }`}
            data-testid={`eval-status-chip-${idx}`}
          >
            <span className="text-sm font-semibold tabular-nums">
              {isActive ? formatDuration(entry!.totalDays) : "0d"}
            </span>
            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-sm ${colorClass}`}>
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
